import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";

const TOP_N = 20;

// Standard amino acid HET codes — excluded because they are protein residues,
// not ligands. Modified residues (e.g. TPO, SEP, PTR) are kept.
const STANDARD_AMINO_ACIDS = new Set([
  "ALA", "ARG", "ASN", "ASP", "CYS",
  "GLN", "GLU", "GLY", "HIS", "ILE",
  "LEU", "LYS", "MET", "PHE", "PRO",
  "SER", "THR", "TRP", "TYR", "VAL",
]);

const OTHER_EXCLUDED = new Set([
  "HOH",          // water
  // "MSE",       // selenomethionine — uncomment to exclude
]);

const isExcluded = (het) =>
  STANDARD_AMINO_ACIDS.has(het) || OTHER_EXCLUDED.has(het);

const AnchorResiduesChart = ({ ligandData }) => {
  const [plotData, setPlotData] = useState([]);
  const [layout, setLayout] = useState({});

  useEffect(() => {
    if (!Array.isArray(ligandData) || ligandData.length === 0) {
      setPlotData([]);
      return;
    }

    const residueLigands = new Map();
    const residueAA = new Map();

    ligandData.forEach((row) => {
      const het = (row?.ligand || "").trim().toUpperCase();
      if (isExcluded(het)) return;

      const posStr = row?.anPosition?.split('_')?.[1];
      const pos = parseInt(posStr, 10);
      if (isNaN(pos)) return;

      const ligKey = (row?.inchikey || row?.ligand || "").trim();
      if (!ligKey) return;

      if (!residueLigands.has(pos)) residueLigands.set(pos, new Set());
      residueLigands.get(pos).add(ligKey);

      if (row?.details && !residueAA.has(pos)) {
        const firstHalf = row.details.split('|')[0]?.trim();
        const tokens = firstHalf?.split('/');
        if (tokens && tokens.length >= 4) {
          residueAA.set(pos, tokens[3].trim().toUpperCase());
        }
      }
    });

    const ranked = Array.from(residueLigands.entries())
      .map(([pos, ligands]) => ({
        pos,
        nLigands: ligands.size,
        aa: residueAA.get(pos) || "?",
      }))
      .sort((a, b) => b.nLigands - a.nLigands)
      .slice(0, TOP_N);

    if (ranked.length === 0) {
      setPlotData([]);
      return;
    }

    const ordered = [...ranked].reverse();
    const yLabels = ordered.map((r) => `${r.aa} ${r.pos}`);
    const xValues = ordered.map((r) => r.nLigands);
    const customData = ordered.map((r) => [r.pos, r.aa]);

    setPlotData([
      {
        x: xValues,
        y: yLabels,
        customdata: customData,
        type: "bar",
        orientation: "h",
        hovertemplate:
          "Residue: %{customdata[1]} %{customdata[0]}<br># of distinct ligands: %{x}<extra></extra>",
        marker: {
          color: xValues,
          colorscale: [
            [0, "#a3e4d7"],
            [1, "#117864"],
          ],
          line: { color: "#333", width: 0.5 },
        },
      },
    ]);

    setLayout({
      autosize: true,
      font: { size: 9 },
      title: { text: `Top ${ranked.length} Anchor Residues` },
      xaxis: {
        title: { text: "# of distinct ligands contacted" },
      },
      yaxis: {
        type: "category",
        title: { text: "Residue" },
        automargin: true,
        tickmode: "linear",   // force every category to show
        dtick: 1,             // step of 1 between ticks
      },
      margin: { t: 50, b: 60, l: 80, r: 40 },
    });
  }, [ligandData]);

  return (
    <div className="aspect-square card">
      <Plot
        data={plotData}
        layout={layout}
        useResizeHandler
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};

export default AnchorResiduesChart;